import { TransactionFilterRequest } from "@/models/transaction";
import { Grid, FormControl, Box } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InputFilterFieldStyled from "../styled/input.filter.field.styled";
import InputLabelStyled from "../styled/input.label.styled";
import BlueButtonStyled from "../styled/blue.button.styled";
import DatePickerComponent from "../common/date.picker.component";
import { Moment } from "moment";
import TransactionTypeSelect from "../common/transaction.type.select";
import DigitalWalletStatusSelect from "../common/digital.wallet.status.select";
import StatusSelect from "../common/status.select";
import BaseInput from "../common/baseInput";
import NumericFormatCustom from "../common/numericFormatCustom";

interface ITransactionsFilter {
    searchClick: Function;
    filter: TransactionFilterRequest;
    setFilter: Function;
}

const TransactionsFilter: FC<ITransactionsFilter> = ({ searchClick, filter, setFilter }: ITransactionsFilter) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);
    const onKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            searchClick();
        }
    };
    const [amount, setAmount] = useState("");
    function clearFilter() {
        setAmount("");
        setFilter(() => ({
            ...new TransactionFilterRequest(filter.walletId),
        }));
        searchClick();
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            if (amount === "") {
                setFilter((prevData: TransactionFilterRequest) => ({
                    ...prevData,
                    amount: null,
                }));
            } else {
                setFilter((prevData: TransactionFilterRequest) => ({
                    ...prevData,
                    amount: Number(amount),
                }));
            }
        }
        return () => {
            ignore = true;
        };
    }, [amount]);

    return (
        <Grid>
            <Grid
                sx={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(64, 82, 137, 0.25)",
                    borderRadius: "12px",
                    padding: "15px",
                    width: "100%",
                    margin: "20px 0",
                }}
            >
                <Grid sx={{ marginBottom: "5px" }}>
                    <InputLabelStyled
                        shrink
                        disableAnimation
                        size="normal"
                        htmlFor="searchFilter-freetext"
                        className="free-text-label"
                    >
                        {t("general.SearchFilterHeader")}
                    </InputLabelStyled>
                </Grid>
                <Grid
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "15px",
                    }}
                >
                    <FormControl variant="standard" sx={{ width: "14%" }}>
                        <BaseInput
                            label={t("general.searchByAmount")}
                            type="search"
                            size="small"
                            value={amount}
                            onKeyDown={onKeyPressHandler}
                            onChange={(event) => setAmount(event.target.value)}
                            InputProps={{
                                inputComponent: NumericFormatCustom as any,
                            }}
                            fullWidth
                        />
                    </FormControl>
                    <FormControl size="small" sx={{ width: "15%" }}>
                        <InputFilterFieldStyled
                            label={t("forms.UserName")}
                            type="search"
                            size="small"
                            value={filter.userName ?? ""}
                            onKeyDown={onKeyPressHandler}
                            onChange={(event) =>
                                setFilter(() => ({
                                    ...filter,
                                    userName: event.target.value,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl variant="standard" sx={{ width: "15%" }}>
                        <DatePickerComponent
                            title={t("orders.fromDate")}
                            value={filter.fromDate}
                            updateDate={(args: Moment | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    fromDate: args,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl variant="standard" sx={{ width: "15%" }}>
                        <DatePickerComponent
                            title={t("orders.endDate")}
                            value={filter.tillDate}
                            minDate={filter.fromDate}
                            updateDate={(args: Moment | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    tillDate: args,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl size="small" sx={{ width: "10%" }}>
                        <TransactionTypeSelect
                            value={filter.transactionType}
                            title={t("employee.Wallet.TransType")}
                            updateStatus={(args?: string | null) => {
                                setFilter(() => ({
                                    ...filter,
                                    transactionType: args,
                                }));
                            }}
                        ></TransactionTypeSelect>
                    </FormControl>
                    <FormControl size="small" sx={{ width: "15%" }}>
                        <DigitalWalletStatusSelect
                            value={filter.walletStatus}
                            title={t("general.SearchFilterWalletStatus")}
                            updateStatus={(args?: string | null) => {
                                setFilter(() => ({
                                    ...filter,
                                    walletStatus: args,
                                }));
                            }}
                        ></DigitalWalletStatusSelect>
                    </FormControl>

                    <FormControl size="small" sx={{ width: "15%" }}>
                        <InputFilterFieldStyled
                            label={t("transaction.TransactionId")}
                            type="search"
                            size="small"
                            value={filter.transactionId}
                            onKeyDown={onKeyPressHandler}
                            onChange={(event) =>
                                setFilter(() => ({
                                    ...filter,
                                    transactionId: event.target.value,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl
                        sx={{
                            width: "7%",
                            display: "flex",
                            flexDirection: "column-reverse",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                            }}
                            className="link-element clear-all-btn"
                            onClick={() => {
                                clearFilter();
                            }}
                        >
                            {t("display.ClearAll")}
                        </Box>
                    </FormControl>
                    <FormControl sx={{ width: "7%" }}>
                        <BlueButtonStyled onClick={() => searchClick()} sx={{ width: "100%", maxWidth: "110px" }}>
                            {t("general.filter")}
                        </BlueButtonStyled>
                    </FormControl>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TransactionsFilter;
